import React, { useEffect, useState } from "react";
import {
    fetchMySubscriptions,
    pauseSub,
    resumeSub,
    cancelSub,
    applyFreeWeek,
    fetchMyReferral,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
    Receipt,
    Sparkles,
    Pause,
    Play,
    XCircle,
    Copy,
    Gift,
    LogOut,
    User as UserIcon,
    CreditCard,
    HelpCircle,
} from "lucide-react";
import { toast } from "sonner";

const StatusPill = ({ status }) => {
    const map = {
        active: ["bg-[#E1FF01] text-[#0A0A0A]", "Active"],
        paused: ["bg-white/10 text-white border border-white/20", "Paused"],
        canceled: ["bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/30", "Canceled"],
        expired: ["bg-white/5 text-white/50 border border-white/10", "Expired"],
    };
    const [cls, label] = map[status] || map.active;
    return (
        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${cls}`}>
            {label}
        </span>
    );
};

const SubscriptionsPage = () => {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [subs, setSubs] = useState([]);
    const [referral, setReferral] = useState(null);
    const [busy, setBusy] = useState(false);

    const reload = async () => {
        try {
            const [s, r] = await Promise.all([fetchMySubscriptions(), fetchMyReferral()]);
            setSubs(s);
            setReferral(r);
        } catch {}
    };

    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate("/login", { state: { next: "/orders" } });
            return;
        }
        reload();
    }, [user, loading, navigate]);

    const doAction = async (fn, msg) => {
        setBusy(true);
        try {
            await fn();
            await reload();
            toast.success(msg);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Something went wrong");
        } finally {
            setBusy(false);
        }
    };

    const copyReferralLink = () => {
        if (!referral?.share_url) return;
        navigator.clipboard?.writeText(referral.share_url);
        toast.success("Referral link copied");
    };

    const goToBillingPortal = () => {
        navigate("/billing");
    };

    if (loading || !user) {
        return (
            <div className="min-h-[100dvh] flex items-center justify-center bg-[#0A0A0A] text-white/60 text-sm">
                Loading…
            </div>
        );
    }

    return (
        <div data-testid="orders-page" className="min-h-[100dvh] pb-32 bg-[#0A0A0A]">
            <header className="px-5 pt-8 pb-4">
                <div className="flex items-center justify-between mb-1">
                    <p className="text-xs uppercase tracking-[0.3em] text-[#E1FF01]">Account</p>
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="open-support"
                            onClick={() => navigate("/support")}
                            className="text-[10px] uppercase tracking-widest text-white/60 hover:text-white flex items-center gap-1"
                        >
                            <HelpCircle size={12} /> Help
                        </button>
                        <button
                            data-testid="logout-btn"
                            onClick={async () => {
                                await logout();
                                navigate("/");
                            }}
                            className="text-[10px] uppercase tracking-widest text-white/50 hover:text-white flex items-center gap-1"
                        >
                            <LogOut size={12} /> Sign out
                        </button>
                    </div>
                </div>
                <h1 className="font-display text-5xl uppercase leading-none">My subs.</h1>
            </header>

            <div className="px-5 mb-4">
                <div className="flex items-center gap-3 bg-[#141414] border border-neutral-800 rounded-2xl p-3.5">
                    {user.picture ? (
                        <img src={user.picture} alt="" className="w-11 h-11 rounded-full object-cover border border-white/20" />
                    ) : (
                        <div className="w-11 h-11 rounded-full bg-[#E1FF01] flex items-center justify-center">
                            <UserIcon size={20} className="text-[#0A0A0A]" />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate" data-testid="user-name">{user.name}</p>
                        <p className="text-xs text-white/50 truncate">{user.email}</p>
                    </div>
                </div>
            </div>

            {/* Manage billing */}
            <div className="px-5 mb-4">
                <button
                    data-testid="manage-billing"
                    onClick={goToBillingPortal}
                    disabled={busy}
                    className="w-full bg-[#141414] hover:bg-[#1a1a1a] border border-neutral-800 hover:border-[#E1FF01]/30 rounded-xl p-3 flex items-center gap-3 transition disabled:opacity-50"
                >
                    <span className="w-9 h-9 rounded-full bg-[#E1FF01]/10 flex items-center justify-center">
                        <CreditCard size={16} className="text-[#E1FF01]" />
                    </span>
                    <div className="flex-1 text-left">
                        <p className="font-semibold text-sm">Billing & receipts</p>
                        <p className="text-[10px] text-white/50 uppercase tracking-widest">
                            Past charges · update card via support
                        </p>
                    </div>
                    <span className="text-white/40">→</span>
                </button>
            </div>

            {/* Referral block */}
            <div className="px-5 mb-6">
                <div className="bg-gradient-to-br from-[#E1FF01]/10 to-transparent border border-[#E1FF01]/30 rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Gift size={16} className="text-[#E1FF01]" />
                        <p className="font-display text-lg uppercase tracking-wider">Earn free weeks</p>
                    </div>
                    <p className="text-xs text-white/70 mb-3">
                        Share your code. When a friend subscribes, you both win — you get
                        <span className="text-[#E1FF01] font-bold"> 1 free week</span> on any sub.
                    </p>
                    <div className="flex items-center gap-2 mb-3">
                        <div className="flex-1 bg-[#0A0A0A] border border-neutral-800 rounded-md px-3 py-2 font-display text-xl tracking-widest text-[#E1FF01]" data-testid="referral-code">
                            {referral?.referral_code || "…"}
                        </div>
                        <button
                            data-testid="copy-referral"
                            onClick={copyReferralLink}
                            className="bg-[#E1FF01] text-[#0A0A0A] font-display text-sm uppercase tracking-widest px-4 py-2 rounded-md flex items-center gap-1.5"
                        >
                            <Copy size={14} /> Copy
                        </button>
                    </div>
                    <div className="flex items-center justify-between text-[11px] uppercase tracking-widest">
                        <span className="text-white/50">
                            {referral?.referrals_count || 0} referrals
                        </span>
                        <span className="text-[#E1FF01]" data-testid="free-weeks-balance">
                            {referral?.free_weeks_balance || 0} free week
                            {(referral?.free_weeks_balance || 0) === 1 ? "" : "s"} ready
                        </span>
                    </div>
                </div>
            </div>

            <div className="px-5">
                {subs.length === 0 ? (
                    <div data-testid="orders-empty" className="text-center py-16">
                        <Receipt size={36} className="mx-auto text-white/30 mb-3" />
                        <p className="font-display text-2xl uppercase">No subscriptions yet</p>
                        <p className="text-sm text-white/50 mt-1 mb-5">
                            Subscribe to a workout to see it here.
                        </p>
                        <button
                            onClick={() => navigate("/")}
                            className="inline-block bg-[#E1FF01] text-[#0A0A0A] font-display text-base uppercase tracking-wider px-5 py-2.5 rounded-md"
                        >
                            Browse plans
                        </button>
                    </div>
                ) : (
                    <ul className="space-y-3">
                        {subs.map((s) => {
                            const it = s.items?.[0] || {};
                            const cpe = new Date(s.current_period_end);
                            return (
                                <li
                                    key={s.id}
                                    data-testid={`sub-${s.id}`}
                                    className="bg-[#141414] border border-neutral-800 rounded-2xl p-4"
                                >
                                    <div className="flex gap-3 items-start">
                                        <img
                                            src={it.image_url}
                                            alt={it.workout_title}
                                            className="w-16 h-16 object-cover rounded-lg"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className="font-display text-lg uppercase leading-tight">
                                                    {it.workout_title}
                                                </p>
                                                <StatusPill status={s.status} />
                                            </div>
                                            <p className="text-[11px] uppercase tracking-widest text-white/50 mt-1">
                                                {it.tier_name} · ${it.price}/{it.period}
                                            </p>
                                            <p className="text-[11px] text-white/40 mt-1">
                                                {s.status === "canceled"
                                                    ? "Access ends "
                                                    : "Renews "}
                                                {cpe.toLocaleDateString()}
                                            </p>
                                            {s.free_week_applied && (
                                                <p className="text-[10px] text-[#E1FF01] mt-0.5 flex items-center gap-1">
                                                    <Sparkles size={10} /> Free week applied
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-neutral-800">
                                        {s.status === "active" && (
                                            <>
                                                <button
                                                    data-testid={`pause-${s.id}`}
                                                    disabled={busy}
                                                    onClick={() =>
                                                        doAction(() => pauseSub(s.id), "Subscription paused")
                                                    }
                                                    className="flex-1 bg-white/5 border border-white/10 text-white py-2 rounded-md text-[11px] uppercase tracking-widest font-bold flex items-center justify-center gap-1.5"
                                                >
                                                    <Pause size={12} /> Pause
                                                </button>
                                                <button
                                                    data-testid={`cancel-${s.id}`}
                                                    disabled={busy}
                                                    onClick={() =>
                                                        doAction(() => cancelSub(s.id), "Subscription canceled")
                                                    }
                                                    className="flex-1 bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] py-2 rounded-md text-[11px] uppercase tracking-widest font-bold flex items-center justify-center gap-1.5"
                                                >
                                                    <XCircle size={12} /> Cancel
                                                </button>
                                            </>
                                        )}
                                        {s.status === "paused" && (
                                            <button
                                                data-testid={`resume-${s.id}`}
                                                disabled={busy}
                                                onClick={() =>
                                                    doAction(() => resumeSub(s.id), "Subscription resumed")
                                                }
                                                className="flex-1 bg-[#E1FF01] text-[#0A0A0A] py-2 rounded-md text-[11px] uppercase tracking-widest font-bold flex items-center justify-center gap-1.5"
                                            >
                                                <Play size={12} /> Resume
                                            </button>
                                        )}
                                        {(referral?.free_weeks_balance || 0) > 0 &&
                                            s.status !== "canceled" && (
                                                <button
                                                    data-testid={`apply-free-week-${s.id}`}
                                                    disabled={busy}
                                                    onClick={() =>
                                                        doAction(
                                                            () => applyFreeWeek(s.id),
                                                            "+7 days added"
                                                        )
                                                    }
                                                    className="flex-1 bg-[#E1FF01]/15 border border-[#E1FF01]/40 text-[#E1FF01] py-2 rounded-md text-[11px] uppercase tracking-widest font-bold flex items-center justify-center gap-1.5"
                                                >
                                                    <Gift size={12} /> +1 week
                                                </button>
                                            )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default SubscriptionsPage;
